<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import {
  Check,
  Close,
  Loading,
  Refresh,
  Setting as SettingIcon,
} from '@element-plus/icons-vue';
import { useFVStore } from '../../stores/formvibes';

// Extract global variables
const { settings_default: settingsDefault, settings_saved: settingsSaved } =
  window.fvGlobalVar;

// Use Pinia store
const store = useFVStore();
const { isSaving } = storeToRefs(store);

// Reactive variables for settings and initial state
const settings = ref([]);
const initialSettings = ref([]);

// Initialize settings and initialSettings
settings.value = Object.entries(settingsDefault).map(([key, values]) => ({
  label: values.label,
  type: values.type,
  value: settingsSaved[key],
  setting: key,
}));

// Deep clone initial settings for accurate comparison
initialSettings.value = JSON.parse(JSON.stringify(settings.value));

// Computed property to check if settings have changed
const hasChanges = computed(() => {
  return settings.value.some((setting, index) => {
    return setting.value !== initialSettings.value[index].value;
  });
});

// Save settings
const onSaveSetting = () => {
  const params = { ...settingsSaved };

  settings.value.forEach(({ setting, value }) => {
    params[setting] = value;
  });

  store.saveSettings(params);

  // Update initial settings after successful save
  initialSettings.value = JSON.parse(JSON.stringify(settings.value));
};

// Reset settings to their initial state
const resetSetting = () => {
  store.resetSettings();
  setTimeout(() => {
    window.location.reload();
  }, 1000);
};

// Sync local settings to store immediately for reactive tab visibility
watch(
  settings,
  (newVal) => {
    const updatedSettings = { ...store.settings };
    newVal.forEach(({ setting, value }) => {
      updatedSettings[setting] = value;
    });
    store.updateSetting(updatedSettings);
  },
  { deep: true }
);
</script>

<template>
  <div>
    <div>
      <ul>
        <li v-for="(setting, index) in settings" :key="index">
          <div>
            <p v-if="setting.type !== 'input'">{{ setting.label }}</p>
            <p
              v-if="
                setting.type === 'input' &&
                settings.filter((s) => s.setting === 'auto_refresh')[0]?.value
              "
            >
              {{ setting.label }}
              <span>( In seconds )</span>
            </p>
            <div v-if="setting.setting === 'enable_mcp_server' && setting.value && !initialSettings[index].value" class="fv-mcp-notice" >Save before switching to MCP Server Config tab</div>
          </div>
          <div>
            <el-switch
              v-if="setting.type === 'toggle'"
              v-model="setting.value"
              style="margin-left: 24px"
              inline-prompt
              :active-icon="Check"
              :inactive-icon="Close"
            />
            <el-input
              v-if="
                setting.type === 'input' &&
                settings.filter((s) => s.setting === 'auto_refresh')[0]?.value
              "
              v-model="setting.value"
              inline-prompt
            />
          </div>
        </li>
      </ul>
      <div>
        <el-button
          type="primary"
          class="fv-save-button"
          :disabled="!hasChanges || isSaving"
          @click="onSaveSetting"
        >
          <el-icon v-if="!isSaving"><SettingIcon /></el-icon>
          <el-icon v-else class="is-loading"><Loading /></el-icon>
          Save Setting
        </el-button>
        <el-button
          type="danger"
          class="fv-reset-button"
          :icon="Refresh"
          plain
          @click="resetSetting"
        >
          Reset
        </el-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fv-save-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.fv-mcp-notice {
  font-size: 13px;
  color: #ff8d4d;
  background-color: #ffe6c78f;
  border-left: 4px solid #ff8d4d;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 2px;
  box-shadow: 0 1px 1px rgba(0,0,0,0.05);
}
</style>